import React, { memo, useCallback } from 'react';

import { ThreeDStockProductsListItemProduct } from './ThreeDStockProductsListItem.types';
import { TeamNanoID } from '../../../../teams/teamsTypes';
import {
  ChangeProductsFiltersFunc,
  FetchProductsFilters,
  OnProductSimilarSearchAction,
  ProductNanoID,
  UpdateProductCacheAction
} from '../../../../products/productsTypes';
import { OnLightboxOpenAction } from '../../../../../helpers/LightboxWrapper';
import { ThreeDStockProductsListItemInfoProductsSet } from './components/ThreeDStockProductsListItemInfo/ThreeDStockProductsListItemInfo.types';
import {
  FetchProductsSetsCacheKeys,
  UpdateProductsSetCacheAction
} from '../../../../productsSets/productsSetsTypes';

import { ThreeDStockProductsListItemPreview } from './components/ThreeDStockProductsListItemPreview';
import { ThreeDStockProductsListItemInfo } from './components/ThreeDStockProductsListItemInfo';

import { ProductPath } from '../../../../products/ProductPath';
import { TeamPath } from '../../../../teams/TeamPath';

type ThreeDStockProductsListItemProps = {
  product: ThreeDStockProductsListItemProduct;
  updateProductCache?: UpdateProductCacheAction<ThreeDStockProductsListItemProduct>;
  onProductEditButtonMouseEnter?: (productNanoId: ProductNanoID) => void;
  onLightboxOpen: OnLightboxOpenAction;
  productsSet: ThreeDStockProductsListItemInfoProductsSet | null;
  productsSetCacheKeys?: FetchProductsSetsCacheKeys;
  updateProductsSetCache: UpdateProductsSetCacheAction<ThreeDStockProductsListItemInfoProductsSet>;
  onProductSimilarSearchFilter?: OnProductSimilarSearchAction;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  changeProductsFilters: ChangeProductsFiltersFunc;
  productsFilters: FetchProductsFilters;
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  findSimilarButtonAction: string;
  editButtonAction: string;
};

function ThreeDStockProductsListItem({
  product,
  updateProductCache,
  onProductEditButtonMouseEnter,
  onLightboxOpen,
  productsSet,
  updateProductsSetCache,
  productsSetCacheKeys,
  onProductSimilarSearchFilter,
  companyNanoId,
  isMyLibrary,
  changeProductsFilters,
  productsFilters,
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  findSimilarButtonAction,
  editButtonAction
}: ThreeDStockProductsListItemProps) {
  const handleOpenLightbox = useCallback(() => {
    if (product.image) {
      onLightboxOpen(product);
    }
  }, [product, onLightboxOpen]);

  const handleEditButtonMouseEnter = useCallback<() => void>(() => {
    onProductEditButtonMouseEnter?.(product.nanoId);
  }, [onProductEditButtonMouseEnter, product.nanoId]);

  let productPagePath = ProductPath.show(product.nanoId);
  let productEditPagePath = ProductPath.edit(product.nanoId);

  if (isMyLibrary) {
    productPagePath = TeamPath.currentCompanyNewLibraryProduct(product.nanoId);
  } else if (companyNanoId) {
    productPagePath = TeamPath.companyNewLibraryProduct(
      companyNanoId,
      product.nanoId
    );
    productEditPagePath = TeamPath.editCompanyNewLibraryProduct(
      companyNanoId,
      product.nanoId
    );
  }

  return (
    <div className="w-120 max-w-full flex flex-col relative rounded-md group">
      <div className="group rounded-xl bg-gray-100 dark:bg-gray-800 border dark:border-gray-700 has-[:checked]:ring-8 has-[:checked]:border-blue-500 dark:has-[:checked]:border-blue-500 has-[:checked]:bg-blue-500/20 dark:has-[:checked]:bg-blue-500/20 overflow-hidden relative flex flex-col">
        <ThreeDStockProductsListItemInfo
          product={product}
          productsSet={productsSet}
          updateProductsSetCache={updateProductsSetCache}
          productsSetCacheKeys={productsSetCacheKeys}
          productPagePath={productPagePath}
          changeProductsFilters={changeProductsFilters}
          productsFilters={productsFilters}
        />

        <ThreeDStockProductsListItemPreview
          downloadButtonAction={downloadButtonAction}
          downloadSeparatelyButtonAction={downloadSeparatelyButtonAction}
          findSimilarButtonAction={findSimilarButtonAction}
          editButtonAction={editButtonAction}
          onEditButtonMouseEnter={handleEditButtonMouseEnter}
          onLightboxOpen={handleOpenLightbox}
          onProductSimilarSearchFilter={onProductSimilarSearchFilter}
          product={product}
          productEditPagePath={productEditPagePath}
          updateProductCache={updateProductCache}
          companyNanoId={companyNanoId}
        />
      </div>
    </div>
  );
}

export default memo<ThreeDStockProductsListItemProps>(
  ThreeDStockProductsListItem
);
